import { IEmailTemplateChild } from 'shared';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import { When } from 'react-if';

import { Button } from '../../../../common/Atoms/Button';
import { SmallSpinner } from '../../../../common/Atoms/SmallSpinner';
import { UnderlineTabs } from '../../../../common/Atoms/UnderlineTabs';
import {
  useCreateEmailTemplateChildMutation,
  useDeleteEmailTemplateMutation,
  useGenerateEmailTemplateChildrenMutation,
} from '../../../services/api/emailTemplates/emailTemplates';

import { VARIATIONS_EXPLAINER } from './constants';

const MAX_TEMPLATE_VARIATIONS = 10;

interface EmailTemplateVariationsProps {
  organisationId: string;
  templateId: string;
  variations: IEmailTemplateChild[];
  /** Ids of variations with unsaved edits */
  unsavedVariationIds: string[];
  isDirty: boolean;
  activeVariationId: string | null;
  onActiveVariationChange: (id: string | null) => void;
}

export function EmailTemplateVariations({
  organisationId,
  templateId,
  variations,
  unsavedVariationIds,
  isDirty,
  activeVariationId,
  onActiveVariationChange,
}: EmailTemplateVariationsProps) {
  const [ createVariation, { isLoading: isCreating } ] = useCreateEmailTemplateChildMutation();
  const [ generateVariation, { isLoading: isGenerating } ] = useGenerateEmailTemplateChildrenMutation();
  const [ deleteVariation, { isLoading: isDeleting } ] = useDeleteEmailTemplateMutation();
  const reachedLimit = variations.length >= MAX_TEMPLATE_VARIATIONS;
  const isAdding = isCreating || isGenerating;

  const tabs = [
    {
      name: `Original`,
      href: ``,
      current: !activeVariationId,
      onClick: () => onActiveVariationChange(null),
    },
    ...variations.map((variation, i) => ({
      name: `Variation ${i + 1}`,
      href: ``,
      current: variation.id === activeVariationId,
      onClick: () => onActiveVariationChange(variation.id),
      badge: unsavedVariationIds.includes(variation.id) ? { text: `Unsaved`, color: `orange` } : undefined,
    })),
  ];

  // Request errors are toasted by the RTK middleware
  async function handleCreate() {
    const result = await createVariation({ org: organisationId, id: templateId });

    if (result.data) {
      onActiveVariationChange(result.data.id);
    }
  }

  async function handleGenerate() {
    const { data } = await generateVariation({ org: organisationId, id: templateId });

    if (data?.failed) {
      toast.error(data.errorMessage || `AI variation generation failed`);

      return;
    }

    if (data?.id) {
      onActiveVariationChange(data.id);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete this variation?`)) {
      return;
    }

    const result = await deleteVariation({ org: organisationId, id: activeVariationId });

    if (!result.error) {
      onActiveVariationChange(null);
    }
  }

  return (
    <div className={ `flex items-center border-b border-gray-200` }>
      <UnderlineTabs
        tabs={ tabs }
        navigational={ false }
        className={ `min-w-0 border-b-0` }
      />
      <Menu>
        <Tooltip title={ reachedLimit
          ? `Maximum of ${MAX_TEMPLATE_VARIATIONS} variations reached`
          : variations.length
            ? `Create variation`
            : `Create a variation. ${VARIATIONS_EXPLAINER}` }>
          { /* Styled like an inactive tab. The span keeps the tooltip working while the button is disabled */ }
          <span className={ `flex-none` }>
            <MenuButton
              disabled={ reachedLimit || isAdding }
              aria-label={ `Create variation` }
              className={ `-mb-px inline-flex items-center border-b-2 border-transparent px-4 py-3 text-gray-500 hover:border-gray-300 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-transparent` }
            >
              { isAdding ? <SmallSpinner className={ `h-5 w-5 text-gray-500` } /> : <PlusIcon className={ `h-5 w-5` } /> }
              <When condition={ isGenerating }>
                <span className={ `ml-2 text-sm` }>{ `Generating variation...` }</span>
              </When>
            </MenuButton>
          </span>
        </Tooltip>
        <MenuItems
          anchor={ `bottom start` }
          className={ `z-10 mt-1 rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-hidden` }
        >
          <MenuItem>
            <button
              type={ `button` }
              onClick={ handleCreate }
              className={ `block w-full px-4 py-2 text-left text-gray-700 data-focus:bg-gray-100` }
            >
              { `Create variation manually` }
            </button>
          </MenuItem>
          <MenuItem>
            <button
              type={ `button` }
              onClick={ handleGenerate }
              className={ `block w-full px-4 py-2 text-left text-gray-700 data-focus:bg-gray-100` }
            >
              { `Create variation with AI` }
              <When condition={ isDirty }>
                <span className={ `block text-xs text-gray-500` }>{ `Uses the saved template, save first for best results` }</span>
              </When>
            </button>
          </MenuItem>
        </MenuItems>
      </Menu>
      <When condition={ !!activeVariationId }>
        <div className={ `ml-auto flex-none pl-3` }>
          <Button
            size={ `sm` }
            color={ `ghost` }
            tooltip={ `Delete variation` }
            onClick={ handleDelete }
            disabled={ isDeleting }
            isIcon
            className={ `h-6 w-6 justify-center px-0` }
          >
            <TrashIcon className={ `h-4 w-4` } />
          </Button>
        </div>
      </When>
    </div>
  );
}
